import {
  buildLittlebirdDraftPrompt,
  type ReferralRewardType,
} from '@littlebird/ui-utils';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { trackEvent } from '@/utils/analytics';
import { navigate } from '@/utils/navigation-service';

/**
 * Opens a new chat where Littlebird drafts the referral note from the user's
 * own usage. Uses the `/chats?auto_prompt=` route (race-free composer
 * prefill) via the navigation service, since the referral modal renders
 * outside the router's React tree.
 */
export function DraftWithLittlebird({
  code,
  isActivity,
  onNavigateAway,
}: {
  code: string | undefined;
  isActivity: boolean;
  onNavigateAway: () => void;
}) {
  const handleDraft = () => {
    if (!code) return;
    const rewardType: ReferralRewardType = isActivity
      ? 'activity_based'
      : 'payment_based';
    trackEvent('REFERRAL_SHARE_BUTTON_CLICKED', {
      platform: 'littlebird_draft',
      experiment_variant: rewardType,
    });
    onNavigateAway();
    navigate({
      to: '/chats',
      search: {
        auto_prompt: buildLittlebirdDraftPrompt(code),
      },
    });
  };

  return (
    <XStack className="items-center gap-3 rounded-xl border border-border-strong p-4">
      <Icons.sparkle className="size-5 text-brand-jordy-blue shrink-0" />
      <YStack className="flex-1 gap-0.5">
        <p className="text-sm font-medium">Let Littlebird write it</p>
        <p className="text-xs text-foreground-muted">
          A personal note drafted from how you actually use it.
        </p>
      </YStack>
      <Button variant="outline" disabled={!code} onClick={handleDraft}>
        Draft note
      </Button>
    </XStack>
  );
}
